// PLAN-cockpit-v2-iter1 Phase 6-B (2026-05-11): Explore Lens の Saved searches CRUD
// localStorage 永続化、cross-tab 同期は v1 では行わない
import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'cockpit_v2.saved_searches'

export interface SavedSearch {
  id: string         // UUID-ish 文字列 (Date.now+random で十分)
  name: string
  q: string
  mode: 'hybrid' | 'fts' | 'embedding'
  domain: string     // '' = All
  origin: string     // '' = All
  created_at: string
}

function loadAll(): SavedSearch[] {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY)
    if (!v) return []
    const arr = JSON.parse(v)
    return Array.isArray(arr) ? arr.filter(isSavedSearch) : []
  } catch { return [] }
}

function isSavedSearch(x: unknown): x is SavedSearch {
  if (!x || typeof x !== 'object') return false
  const o = x as Record<string, unknown>
  return typeof o.id === 'string' && typeof o.name === 'string' && typeof o.q === 'string'
}

function persist(items: SavedSearch[]) {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items)) } catch { /* ignore */ }
}

export function useSavedSearches() {
  const [items, setItems] = useState<SavedSearch[]>(loadAll)

  // 他コンポーネントからの変更を listen
  useEffect(() => {
    const handler = () => setItems(loadAll())
    window.addEventListener('saved_searches_change', handler)
    return () => window.removeEventListener('saved_searches_change', handler)
  }, [])

  const save = useCallback((s: Omit<SavedSearch, 'id' | 'created_at'>) => {
    const next: SavedSearch = {
      ...s,
      id: `ss_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      created_at: new Date().toISOString(),
    }
    const updated = [next, ...loadAll()].slice(0, 50)
    persist(updated)
    setItems(updated)
    window.dispatchEvent(new CustomEvent('saved_searches_change'))
    return next
  }, [])

  const remove = useCallback((id: string) => {
    const updated = loadAll().filter(x => x.id !== id)
    persist(updated)
    setItems(updated)
    window.dispatchEvent(new CustomEvent('saved_searches_change'))
  }, [])

  return { items, save, remove }
}
